<template>
  <b-form @submit.prevent="handleSubmit">
    <b-row>
      <b-col cols="6">
        <basic-input
          :value.sync="form.code"
          name="code"
          label="Mã"
          placeholder="VD: VC1000"
          validate="required"
          v-validate="'required'"
          data-vv-as="Mã"
        />
        <span class="text-danger">{{ errors.first('code') }}</span>
      </b-col>
      <b-col cols="6">
        <basic-input
          :value.sync="form.shortName"
          name="shortName"
          label="Tên viết tắt"
          placeholder="VD: VC1000"
          validate="required"
          v-validate="'required'"
          data-vv-as="Tên viết tắt"
        />
        <span class="text-danger">{{ errors.first('shortName') }}</span>
      </b-col>
      <b-col cols="6">
        <basic-input
          :value.sync="form.name"
          name="name"
          label="Tên sản phẩm"
          placeholder="VD: Vitamin C 1000mg"
          validate="required"
          v-validate="'required'"
          data-vv-as="Tên"
        />
        <span class="text-danger">{{ errors.first('name') }}</span>
      </b-col>

      <b-col cols="6">
        <label>Mã nhóm</label>
        <div ref="groupDropdownContainer" class="position-relative">
          <input
            v-model="groupSearchText"
            type="text"
            class="form-control"
            placeholder="Tìm kiếm hoặc nhập mã nhóm..."
            @input="onGroupSearch"
            @focus="showGroupDropdown = true"
          />
          <div v-if="showGroupDropdown" class="dropdown-menu show position-absolute w-100 group-dropdown" style="top: 100%; left: 0; z-index: 1000; max-height: 320px; overflow-y: auto;">
            <div v-if="filteredGroups.length > 0">
              <a
                v-for="group in filteredGroups"
                :key="group.value"
                href="#"
                class="dropdown-item"
                @click.prevent="selectGroup(group)"
              >
                {{ group.text }}
              </a>
            </div>
            <div v-if="groupSearchText && filteredGroups.length === 0">
              <div class="dropdown-divider"></div>
              <a
                href="#"
                class="dropdown-item text-success font-weight-bold"
                @click.prevent="openAddGroupModal"
              >
                <i class="ki ki-plus"></i> Thêm mã nhóm: "{{ groupSearchText }}"
              </a>
            </div>
            <div v-if="!groupSearchText && loadedMedicineGroups.length === 0" class="dropdown-item text-muted">
              Không có dữ liệu
            </div>
          </div>
        </div>
      </b-col>

      <b-col cols="6">
        <basic-select
          label="Nhóm"
          :value.sync="selectedIsMedicineObj"
          name="isMedicine"
          placeholder="Chọn loại..."
          :options="medicineTypeOptions"
          track-by="value"
          value-label="text"
          @input="onMedicineTypeSelect"
        />
      </b-col>

      <!-- Hoạt chất: custom dropdown với search + thêm mới -->
      <b-col cols="6">
        <label>Hoạt chất</label>
        <div ref="ingredientDropdownContainer" class="position-relative">
          <!-- Tags hoạt chất đã chọn + ô search -->
          <div
            class="form-control ingredient-select-box"
            @click="focusIngredientSearch"
          >
            <span
              v-for="item in selectedActiveIngredients"
              :key="item.id"
              class="ingredient-tag"
            >
              {{ item.name }}
              <span class="ingredient-tag-remove" @click.stop="removeIngredient(item)">&times;</span>
            </span>
            <input
              ref="ingredientSearchInput"
              v-model="ingredientSearchText"
              type="text"
              class="ingredient-search-input"
              placeholder="Tìm hoạt chất..."
              @input="onIngredientSearch"
              @focus="showIngredientDropdown = true"
            />
          </div>

          <!-- Dropdown danh sách hoạt chất -->
          <div
            v-if="showIngredientDropdown"
            class="dropdown-menu show position-absolute w-100 group-dropdown"
            style="top: 100%; left: 0; z-index: 1000; max-height: 260px; overflow-y: auto;"
          >
            <div v-if="filteredIngredientOptions.length > 0">
              <a
                v-for="ing in filteredIngredientOptions"
                :key="ing.id"
                href="#"
                class="dropdown-item"
                @click.prevent="selectIngredient(ing)"
              >
                {{ ing.name }}
              </a>
            </div>

            <!-- Không tìm thấy → gợi ý thêm mới -->
            <div v-if="ingredientSearchText && filteredIngredientOptions.length === 0">
              <div class="dropdown-divider"></div>
              <a
                href="#"
                class="dropdown-item text-success font-weight-bold"
                @click.prevent="openAddIngredientModal"
              >
                <i class="ki ki-plus"></i> Thêm hoạt chất: "{{ ingredientSearchText }}"
              </a>
            </div>

            <div v-if="!ingredientSearchText && activeIngredients.length === 0" class="dropdown-item text-muted">
              Không có dữ liệu
            </div>
          </div>
        </div>
      </b-col>

      <b-col cols="6">
        <basic-input
          :value.sync="form.strength"
          name="strength"
          label="Hàm lượng"
          placeholder="VD: 1000mg"
          validate="required"
          v-validate="'required'"
          data-vv-as="Hàm lượng"
        />
        <span class="text-danger">{{ errors.first('strength') }}</span>
      </b-col>

      <b-col cols="6">
        <basic-input
          :value.sync="form.unit"
          name="unit"
          label="Đơn vị"
          placeholder="VD: viên, bình, bình xịt, ống..."
        />
      </b-col>

      <b-col cols="6">
        <basic-input
          :value.sync="form.price"
          name="price"
          label="Giá (VNĐ)"
          placeholder="VD: 150000"
          type="number"
          data-vv-as="Giá"
        />
      </b-col>

      <b-col cols="6">
        <basic-select
          label="Trạng thái"
          :value.sync="selectedStatusObj"
          name="status"
          placeholder="Chọn trạng thái"
          :options="statusOptions"
          track-by="value"
          value-label="text"
          @input="onStatusSelect"
        />
      </b-col>

      <b-col cols="12">
        <basic-text-area
          :value.sync="form.description"
          name="description"
          label="Mô tả"
          placeholder="Nhập mô tả chi tiết..."
          :rows="3"
        />
      </b-col>
    </b-row>

    <!-- Modal thêm mã nhóm -->
    <b-modal v-model="showAddGroupModal" title="Thêm mã nhóm mới" centered no-close-on-backdrop>
      <b-form>
        <b-form-group label="Mã nhóm" label-for="newGroupName">
          <b-form-input
            id="newGroupName"
            v-model="newGroupData.name"
            placeholder="Nhập tên mã nhóm..."
            required
          ></b-form-input>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="cancelAddGroup">Hủy</b-button>
        <b-button variant="success" @click="createNewGroup">Thêm mới</b-button>
      </template>
    </b-modal>

    <!-- Modal thêm hoạt chất mới -->
    <b-modal v-model="showAddIngredientModal" title="Thêm hoạt chất mới" centered no-close-on-backdrop>
      <b-form>
        <b-form-group label="Tên hoạt chất" label-for="newIngredientName">
          <b-form-input
            id="newIngredientName"
            v-model="newIngredientData.name"
            placeholder="Nhập tên hoạt chất..."
            required
          ></b-form-input>
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="secondary" @click="cancelAddIngredient">Hủy</b-button>
        <b-button variant="success" :disabled="isCreatingIngredient" @click="createNewIngredient">
          <span v-if="isCreatingIngredient">Đang thêm...</span>
          <span v-else>Thêm mới</span>
        </b-button>
      </template>
    </b-modal>
  </b-form>
</template>

<script>
export default {
  name: 'MedicineForm',
  props: {
    value: {
      type: Object,
      default: () => ({
        id: null,
        name: '',
        shortName: '',
        strength: '',
        unit: '',
        medicineGroupId: '',
        isMedicine: true,
        price: null,
        description: '',
        status: 0,
      }),
    },
  },
  data() {
    return {
      form: { ...this.value },
      selectedGroupObj: null,
      selectedIsMedicineObj: null,
      selectedStatusObj: null,
      statusOptions: [
        { value: 0, text: 'Hoạt động' },
        { value: 1, text: 'Không hoạt động' },
      ],
      selectedActiveIngredients: [],
      loadedMedicineGroups: [],
      activeIngredients: [],
      groupSearchText: '',
      showGroupDropdown: false,
      filteredGroups: [],
      showAddGroupModal: false,
      userEditing: false,
      newGroupData: {
        name: '',
        description: '',
      },
      medicineTypeOptions: [
        { value: true, text: 'Thuốc' },
        { value: false, text: 'Thực phẩm chức năng' },
      ],

      ingredientSearchText: '',
      showIngredientDropdown: false,
      filteredIngredientOptions: [],
      showAddIngredientModal: false,
      isCreatingIngredient: false,
      newIngredientData: {
        name: '',
        description: '',
      },
    };
  },

  created() {
    this.fetchMedicineGroups();
    this.fetchActiveIngredients();
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  },

  watch: {
    value: {
      handler(newVal) {
        // Nếu user đang tương tác (chọn group/ingredient), không ghi đè
        if (this.userEditing) return;

        // Chỉ cập nhật form nếu có sự thay đổi thực sự từ bên ngoài
        const prevGroupId = this.form.medicineGroupId;
        this.form = { ...newVal };
        this.selectedIsMedicineObj =
          this.medicineTypeOptions.find((o) => o.value === newVal.isMedicine) || null;
        this.selectedStatusObj =
          this.statusOptions.find((o) => o.value === newVal.status) || null;

        // Chỉ sync groupSearchText khi medicineGroupId thực sự thay đổi từ parent
        if (newVal.medicineGroupId !== prevGroupId) {
          this.selectedGroupObj =
            this.loadedMedicineGroups.find((g) => g.value === newVal.medicineGroupId) || null;
          if (this.selectedGroupObj) {
            this.groupSearchText = this.selectedGroupObj.text;
          } else {
            this.groupSearchText = '';
          }
        }

        this._syncSelectedIngredients();
      },
      deep: true,
      immediate: true,
    },
    form: {
      handler(newVal) {
        if (JSON.stringify(newVal) !== JSON.stringify(this.value)) {
          this.$emit('input', { ...newVal });
        }
      },
      deep: true,
    },
    loadedMedicineGroups(groups) {
      this.selectedGroupObj =
        groups.find((g) => g.value === this.form.medicineGroupId) || null;
      this.onGroupSearch();
    },
    activeIngredients(list) {
      if (list.length > 0) {
        // Chỉ sync lần đầu khi chưa có hoạt chất nào được chọn,
        // tránh reset tag sau khi tạo hoạt chất mới (re-fetch gây ra watcher này lần 2)
        if (this.selectedActiveIngredients.length === 0) {
          this._syncSelectedIngredients();
        }
        this.onIngredientSearch();
      }
    },
  },

  methods: {

    async fetchMedicineGroups() {
      try {
        const { data } = await this.$api.get('BundleMedicineGroup/All');
        if (Array.isArray(data)) {
          this.loadedMedicineGroups = data.map((g) => ({ value: g.id, text: g.name }));
          sessionStorage.setItem('medicineGroups', JSON.stringify(this.loadedMedicineGroups));
        }
      } catch (error) {
        console.error('Failed to fetch medicine groups:', error);
        const storedGroups = sessionStorage.getItem('medicineGroups');
        if (storedGroups) {
          try {
            const parsed = JSON.parse(storedGroups);
            if (Array.isArray(parsed)) {
              this.loadedMedicineGroups = parsed;
            }
          } catch (e) {
            console.error('Failed to parse medicineGroups from sessionStorage:', e);
          }
        }
      }
    },

    handleClickOutside(event) {
      if (this.$refs.groupDropdownContainer && !this.$refs.groupDropdownContainer.contains(event.target)) {
        this.showGroupDropdown = false;
      }
      if (this.$refs.ingredientDropdownContainer && !this.$refs.ingredientDropdownContainer.contains(event.target)) {
        this.showIngredientDropdown = false;
      }
    },

    onGroupSearch() {
      if (!this.groupSearchText) {
        this.filteredGroups = this.loadedMedicineGroups;
      } else {
        this.filteredGroups = this.loadedMedicineGroups.filter((g) =>
          g.text.toLowerCase().includes(this.groupSearchText.toLowerCase()) ||
          g.value.toString().toLowerCase().includes(this.groupSearchText.toLowerCase())
        );
      }
    },

    selectGroup(group) {
      this.userEditing = true;
      this.selectedGroupObj = group;
      this.form = { ...this.form, medicineGroupId: group.value };
      this.groupSearchText = group.text;
      this.showGroupDropdown = false;
      this.$nextTick(() => { this.userEditing = false; });
    },

    openAddGroupModal() {
      this.newGroupData.name = this.groupSearchText;
      this.showAddGroupModal = true;
    },

    async createNewGroup() {
      if (!this.newGroupData.name) {
        this.$toastr.error('Vui lòng nhập tên mã nhóm', 'Lỗi');
        return;
      }
      // Lưu tên trước await để dùng làm fallback nếu API không trả về id khớp
      const groupName = this.newGroupData.name.trim();
      try {
        const formData = new FormData();
        formData.append('name', groupName);
        if (this.newGroupData.description) {
          formData.append('description', this.newGroupData.description);
        }

        const { data } = await this.$api.post('BundleMedicineGroup', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        await this.fetchMedicineGroups();

        // Ưu tiên tìm theo ID từ response, fallback tìm theo tên nếu API wrap response
        const newGroup =
          (data?.id
            ? this.loadedMedicineGroups.find((g) => String(g.value) === String(data.id))
            : null) ||
          this.loadedMedicineGroups.find((g) => g.text === groupName);

        if (newGroup) {
          this.selectGroup(newGroup);
        }

        this.showAddGroupModal = false;
        this.newGroupData = { name: '', description: '' };
        this.$toastr.success('Thêm mã nhóm thành công', 'Thành công');
      } catch (error) {
        this.$toastr.error('Không thể thêm mã nhóm: ' + error.message, 'Lỗi');
      }
    },

    cancelAddGroup() {
      this.showAddGroupModal = false;
      this.newGroupData = { name: '', description: '' };
      this.groupSearchText = '';
    },

    onGroupSelect(val) {
      const found = this.loadedMedicineGroups.find((g) => g.value === val) || null;
      this.selectedGroupObj = found;
      this.form = { ...this.form, medicineGroupId: found ? found.value : '' };
    },

    onMedicineTypeSelect(val) {
      const found = this.medicineTypeOptions.find((o) => o.value === val) || null;
      this.selectedIsMedicineObj = found;
      this.form = { ...this.form, isMedicine: found !== null ? found.value : true };
    },

    onStatusSelect(val) {
      const found = this.statusOptions.find((o) => o.value === val) || null;
      this.selectedStatusObj = found;
      this.form = { ...this.form, status: found !== null ? found.value : 0 };
    },

    // ─── Active Ingredients ────────────────────────────────────────────────────

    async fetchActiveIngredients() {
      try {
        const { data } = await this.$api.get('ActiveIngredients/All');
        if (Array.isArray(data)) {
          this.activeIngredients = data;
          this.filteredIngredientOptions = data;
          this._syncSelectedIngredients();
        }
      } catch (error) {
        console.error('Failed to fetch active ingredients:', error);
        this.$toastr.error('Không thể tải danh sách hoạt chất', 'Lỗi');
      }
    },

    _syncSelectedIngredients() {
      if (!this.activeIngredients.length) return;
      const medicineActives = this.form.medicineActiveIngredients;
      if (Array.isArray(medicineActives) && medicineActives.length > 0) {
        const ids = medicineActives.map((m) => m.activeIngredientId);
        this.selectedActiveIngredients = this.activeIngredients
          .filter((a) => ids.includes(a.id))
          .map((a) => ({ ...a, text: a.text || a.name }));
        return;
      }
      if (Array.isArray(this.form.activeIngredientIds) && this.form.activeIngredientIds.length > 0) {
        this.selectedActiveIngredients = this.activeIngredients
          .filter((a) => this.form.activeIngredientIds.includes(a.id))
          .map((a) => ({ ...a, text: a.text || a.name }));
        return;
      }
      this.selectedActiveIngredients = [];
    },

    onIngredientSearch() {
      const keyword = (this.ingredientSearchText || '').toLowerCase();
      const selectedIds = this.selectedActiveIngredients.map((s) => s.id);
      this.filteredIngredientOptions = this.activeIngredients.filter(
        (ing) =>
          !selectedIds.includes(ing.id) &&
          ing.name.toLowerCase().includes(keyword)
      );
    },

    focusIngredientSearch() {
      this.showIngredientDropdown = true;
      this.$nextTick(() => {
        if (this.$refs.ingredientSearchInput) {
          this.$refs.ingredientSearchInput.focus();
        }
      });
    },

    selectIngredient(ing) {
      this.userEditing = true;
      const already = this.selectedActiveIngredients.find((s) => s.id === ing.id);
      if (!already) {
        this.selectedActiveIngredients = [...this.selectedActiveIngredients, { ...ing }];
        this._emitIngredients();
      }
      this.ingredientSearchText = '';
      this.onIngredientSearch();
      this.$nextTick(() => { this.userEditing = false; });
    },

    removeIngredient(ing) {
      this.userEditing = true;
      this.selectedActiveIngredients = this.selectedActiveIngredients.filter((s) => s.id !== ing.id);
      this._emitIngredients();
      this.onIngredientSearch();
      this.$nextTick(() => { this.userEditing = false; });
    },

    _emitIngredients() {
      this.form = {
        ...this.form,
        activeIngredientIds: this.selectedActiveIngredients.map((a) => a.id),
        activeIngredients: this.selectedActiveIngredients,
      };
    },

    openAddIngredientModal() {
      this.newIngredientData.name = this.ingredientSearchText;
      this.newIngredientData.description = '';
      this.showIngredientDropdown = false;
      this.showAddIngredientModal = true;
    },

    async createNewIngredient() {
      if (!this.newIngredientData.name || !this.newIngredientData.name.trim()) {
        this.$toastr.error('Vui lòng nhập tên hoạt chất', 'Lỗi');
        return;
      }
      this.isCreatingIngredient = true;
      // Lưu tên trước khi reset để dùng làm fallback nếu API không trả về name
      const ingredientName = this.newIngredientData.name.trim();
      try {
        const formData = new FormData();
        formData.append('name', ingredientName);
        const { data } = await this.$api.post('ActiveIngredients', formData);
        await this.fetchActiveIngredients();
        // Tìm ingredient vừa tạo trong list đã re-fetch:
        // Ưu tiên theo ID từ response, nếu không được thì tìm theo tên chính xác
        const created =
          (data?.id
            ? this.activeIngredients.find((a) => String(a.id) === String(data.id))
            : null) ||
          this.activeIngredients.find((a) => a.name === ingredientName);
        if (created) {
          this.selectIngredient(created);
        }
        this.showAddIngredientModal = false;
        this.newIngredientData = { name: '', description: '' };
        this.ingredientSearchText = '';
        this.$toastr.success('Thêm hoạt chất thành công', 'Thành công');
      } catch (error) {
        this.$toastr.error('Không thể thêm hoạt chất: ' + (error.message || 'Lỗi không xác định'), 'Lỗi');
      } finally {
        this.isCreatingIngredient = false;
      }
    },

    cancelAddIngredient() {
      this.showAddIngredientModal = false;
      this.newIngredientData = { name: '', description: '' };
      this.ingredientSearchText = '';
    },


    onActiveIngredientSelect(val) {
      if (!Array.isArray(val)) val = val ? [val] : [];
      const mapped = val.map((a) => ({ ...a, text: a.text || a.name }));
      this.userEditing = true;
      this.selectedActiveIngredients = mapped;
      this.form = {
        ...this.form,
        activeIngredientIds: mapped.map((a) => a.id),
        activeIngredients: mapped,
      };
      this.$nextTick(() => { this.userEditing = false; });
    },

    getTextMultipleSelect(array) {
      if (!array || !array.length) return null;
      return array.map((el) => el.text || el.name).join(', ');
    },

    async validate() {
      return await this.$validator.validateAll();
    },

    reset() {
      this.form = {
        id: null,
        name: '',
        shortName: '',
        strength: '',
        unit: '',
        medicineGroupId: '',
        activeIngredientIds: [],
        isMedicine: true,
        price: null,
        description: '',
        status: 0,
      };
      this.selectedGroupObj = null;
      this.selectedIsMedicineObj = null;
      this.selectedStatusObj = null;
      this.selectedActiveIngredients = [];
      this.groupSearchText = '';
      this.ingredientSearchText = '';
      this.$validator.reset();
    },
  },
};
</script>

<style scoped>
.group-dropdown {
  max-height: 320px !important;
  overflow-y: auto !important;
  border: 1px solid #dee2e6;
  border-top: none;
  border-radius: 0 0 0.25rem 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.075);
}

.group-dropdown .dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 1rem;
}

.group-dropdown .dropdown-item:hover {
  background-color: #f8f9fa;
}

.group-dropdown .dropdown-divider {
  margin: 0.25rem 0;
}

/* Ingredient multi-select box */
.ingredient-select-box {
  min-height: 38px;
  height: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  cursor: text;
  padding: 4px 8px;
}

.ingredient-tag {
  display: inline-flex;
  align-items: center;
  background-color: #e9ecef;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.85rem;
  white-space: nowrap;
}

.ingredient-tag-remove {
  margin-left: 5px;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: #6c757d;
}

.ingredient-tag-remove:hover {
  color: #dc3545;
}

.ingredient-search-input {
  border: none;
  outline: none;
  flex: 1;
  min-width: 80px;
  font-size: 1rem;
  background: transparent;
  padding: 0;
}
</style>